import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

import * as applePay from "braintree-web/apple-pay";
import * as client from "braintree-web/client";
import * as dataCollector from "braintree-web/data-collector";
import * as hostedFields from "braintree-web/hosted-fields";
import * as paypalCheckout from "braintree-web/paypal-checkout";
import * as venmo from "braintree-web/venmo";
import { ApplePay, ApplePayPayload, ApplePaySession } from "braintree-web/apple-pay";
import { Client } from "braintree-web/client";
import { DataCollector } from "braintree-web/data-collector";
import {
  HostedFieldFieldOptions,
  HostedFields,
  HostedFieldsEvent,
  HostedFieldsTokenizePayload,
} from "braintree-web/hosted-fields";
import {
  PayPalCheckout,
  PayPalCheckoutLoadPayPalSDKOptions,
} from "braintree-web/paypal-checkout";
import { Venmo, VenmoTokenizePayload } from "braintree-web/venmo";

import {
  ApplePayDonationLabel,
  ApplePayMerchantDisplayName,
  ApplePayVersion,
  AppLanguage,
  CardField,
  CardFieldValue,
  DirectPaymentMethod,
  DirectPaymentMethodValue,
  formatPaymentAmount,
  HostedFieldSelector,
  hostedFieldPlaceholdersFor,
  PaymentCancellationCode,
  payPalLocaleFor,
} from "../domain/payment.constants";
import { RepositoryBraintreeService } from "./repository-braintree.service";
import { VenmoReturnService } from "./venmo-return.service";

export type CardFieldSelectors = Partial<Record<CardFieldValue, string>>;

export interface PaymentAvailability {
  card: boolean;
  paypal: boolean;
  venmo: boolean;
  applePay: boolean;
}

export interface CardFieldState {
  empty: boolean;
  valid: boolean;
}

export type CardFieldStates = Partial<Record<CardFieldValue, CardFieldState>>;

export interface CardFieldChange extends CardFieldState {
  visited?: boolean;
}

export type CardFieldChangeHandler = (
  field: CardFieldValue,
  change: CardFieldChange
) => void;

export type PaymentTokenPayload =
  | HostedFieldsTokenizePayload
  | VenmoTokenizePayload
  | ApplePayPayload
  | paypal.TokenizePayload;

interface PayPalButtonsHandle {
  isEligible(): boolean;
  render(container: string | HTMLElement): Promise<void>;
  close(): void;
}

interface PayPalSdk {
  Buttons(options: {
    createBillingAgreement: () => Promise<string>;
    onApprove: (data: paypal.AuthorizationData) => Promise<void>;
    onCancel?: () => void;
    onError?: (error: unknown) => void;
    style: {
      height: number;
      label: string;
      layout: string;
      shape: string;
      tagline: boolean;
    };
  }): PayPalButtonsHandle;
}

type VenmoCreateOptions = Parameters<typeof venmo.create>[0] & {
  allowNonDefaultBrowsers?: boolean;
  allowWebviews?: boolean;
  requireManualReturn?: boolean;
};

const DEFAULT_CARD_FIELD_SELECTORS: CardFieldSelectors = {
  [CardField.CardholderName]: HostedFieldSelector.CardholderName,
  [CardField.Number]: HostedFieldSelector.Number,
  [CardField.ExpirationDate]: HostedFieldSelector.ExpirationDate,
  [CardField.Cvv]: HostedFieldSelector.Cvv,
  [CardField.PostalCode]: HostedFieldSelector.PostalCode,
};

@Injectable({
  providedIn: "root",
})
export class PaymentSdkService {
  private _braintree = inject(RepositoryBraintreeService);
  private readonly _venmoReturn = inject(VenmoReturnService);

  private _client: Client | null = null;
  private _dataCollector: DataCollector | null = null;
  private _hostedFields: HostedFields | null = null;
  private _payPalCheckout: PayPalCheckout | null = null;
  private _payPalButtons: PayPalButtonsHandle | null = null;
  private _venmo: Venmo | null = null;
  private _applePay: ApplePay | null = null;
  private _applePaySession: ApplePaySession | null = null;

  private _payloads: Partial<Record<DirectPaymentMethodValue, PaymentTokenPayload>> =
    {};
  private _applePayAuthorizedAmount: string | null = null;

  private _language = AppLanguage.English as string;
  private _payPalLocale = payPalLocaleFor(AppLanguage.English);
  private _cardFieldHandler: CardFieldChangeHandler | null = null;

  /** Serializes initialize/teardown so the two can never interleave. */
  private _lifecycle: Promise<unknown> = Promise.resolve();

  getClientToken() {
    return this._braintree.getClientToken(true);
  }

  initializePaymentMethods(
    cardFieldSelectors: CardFieldSelectors = DEFAULT_CARD_FIELD_SELECTORS,
    language: string = AppLanguage.English
  ): Promise<PaymentAvailability> {
    return this._enqueue(() => this._initialize(cardFieldSelectors, language));
  }

  teardown(): Promise<void> {
    // Invalidate an outstanding return immediately, even if SDK teardown is queued.
    this._venmoReturn.clear();
    return this._enqueue(() => this._teardownInstances());
  }

  onCardFieldChange(handler: CardFieldChangeHandler | null): void {
    this._cardFieldHandler = handler;
  }

  currentCardFieldValues(): CardFieldStates {
    const states: CardFieldStates = {};
    if (!this._hostedFields) {
      return states;
    }

    const fields = this._hostedFields.getState().fields;
    Object.keys(fields).forEach((key) => {
      states[key as CardFieldValue] = {
        empty: fields[key as CardFieldValue].isEmpty,
        valid: fields[key as CardFieldValue].isValid,
      };
    });

    return states;
  }

  getDeviceData(): string {
    return this._dataCollector?.deviceData || "";
  }

  hasAuthorizedPayment(method: DirectPaymentMethodValue): boolean {
    return !!this._payloads[method];
  }

  async updatePaymentLanguage(language: string): Promise<void> {
    this._language = language;
    this._payPalLocale = payPalLocaleFor(language);

    if (!this._hostedFields) {
      return;
    }

    const placeholders = hostedFieldPlaceholdersFor(language);
    Object.keys(placeholders).forEach((field) => {
      this._hostedFields?.setAttribute({
        field: field as CardFieldValue,
        attribute: "placeholder",
        value: placeholders[field as CardFieldValue],
      });
    });
  }

  renderPayPalButton(
    container: string | HTMLElement,
    onAuthorized: (payload: PaymentTokenPayload) => void,
    onCancel?: () => void,
    onError?: (error: unknown) => void
  ): Promise<void> {
    if (!this._payPalCheckout) {
      return Promise.reject(new Error("PayPal is not available."));
    }

    if (this._payPalButtons) {
      return Promise.resolve();
    }

    const paypalSdk = this._paypalSdkFromWindow();
    if (!paypalSdk?.Buttons) {
      return Promise.reject(new Error("The PayPal SDK did not load."));
    }

    const checkout = this._payPalCheckout;
    const buttons = paypalSdk.Buttons({
      createBillingAgreement: () =>
        checkout.createPayment({
          flow: "vault" as paypal.FlowType,
          locale: this._payPalLocale,
        }),
      onApprove: async (data) => {
        const payload = await checkout.tokenizePayment(data);
        this._payloads[DirectPaymentMethod.PayPal] = payload;
        onAuthorized(payload);
      },
      onCancel: () => onCancel?.(),
      onError: (error) => onError?.(error),
      style: {
        height: 44,
        label: "checkout",
        layout: "horizontal",
        shape: "rect",
        tagline: false,
      },
    });

    if (!buttons.isEligible()) {
      return Promise.reject(new Error("PayPal is not eligible for this buyer."));
    }

    this._payPalButtons = buttons;
    return buttons.render(container).then(
      () => {
        if (this._payPalButtons !== buttons) {
          this._closePayPalButtons(buttons);
        }
      },
      (error: unknown) => {
        if (this._payPalButtons === buttons) {
          this._payPalButtons = null;
        }
        this._closePayPalButtons(buttons);
        throw error;
      }
    );
  }

  async authorizeVenmo(): Promise<VenmoTokenizePayload> {
    if (!this._venmo) {
      throw new Error("Venmo is not available.");
    }

    const instance = this._venmo;
    const payload = await this._venmoReturn.tokenize(() => instance.tokenize());
    this._payloads[DirectPaymentMethod.Venmo] = payload;
    return payload;
  }

  authorizeApplePay(
    amount: number,
    label: string = ApplePayDonationLabel
  ): Promise<ApplePayPayload> {
    if (!this._applePay) {
      return Promise.reject(new Error("Apple Pay is not available."));
    }

    const amountText = formatPaymentAmount(amount);
    const paymentRequest = this._applePay.createPaymentRequest({
      total: { label, amount: amountText },
      requiredBillingContactFields: ["postalAddress"],
    });

    return new Promise((resolve, reject) => {
      const applePaySession = this._applePaySessionCtor();
      if (!applePaySession) {
        reject(new Error("Apple Pay is not available."));
        return;
      }

      const session = new applePaySession(ApplePayVersion, paymentRequest);
      this._applePaySession = session;

      session.onvalidatemerchant = async (event: { validationURL: string }) => {
        try {
          const merchantSession = await this._applePay?.performValidation({
            validationURL: event.validationURL,
            displayName: ApplePayMerchantDisplayName,
          });
          session.completeMerchantValidation(merchantSession);
        } catch (error) {
          session.abort();
          this._applePaySession = null;
          reject(error);
        }
      };

      session.onpaymentauthorized = async (event: {
        payment: { token: unknown };
      }) => {
        try {
          const payload = await this._applePay?.tokenize({
            token: event.payment.token,
          });
          if (!payload) {
            throw new Error("Apple Pay is not available.");
          }
          this._payloads[DirectPaymentMethod.ApplePay] = payload;
          this._applePayAuthorizedAmount = amountText;
          session.completePayment(applePaySession.STATUS_SUCCESS);
          this._applePaySession = null;
          resolve(payload);
        } catch (error) {
          session.completePayment(applePaySession.STATUS_FAILURE);
          this._applePaySession = null;
          reject(error);
        }
      };

      session.oncancel = () => {
        this._applePaySession = null;
        reject({ code: PaymentCancellationCode.ApplePayCanceled });
      };

      session.begin();
    });
  }

  async preparePayment(
    method: DirectPaymentMethodValue,
    amount = 0
  ): Promise<PaymentTokenPayload> {
    if (method === DirectPaymentMethod.Card) {
      if (!this._hostedFields) {
        throw new Error("The card form is not ready.");
      }
      return this._hostedFields.tokenize();
    }

    if (method === DirectPaymentMethod.ApplePay) {
      const authorizedAmount = this._applePayAuthorizedAmount;
      if (authorizedAmount && authorizedAmount !== formatPaymentAmount(amount)) {
        this._clearApplePayAuthorization();
        throw new Error(
          "The donation amount changed. Please authorize Apple Pay again."
        );
      }
    }

    const payload = this._payloads[method];
    if (!payload) {
      throw new Error(`${method} has not been authorized.`);
    }

    this._payloads[method] = undefined;
    if (method === DirectPaymentMethod.ApplePay) {
      this._applePayAuthorizedAmount = null;
    }

    return payload;
  }

  private _enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = this._lifecycle.then(task, task);
    this._lifecycle = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  private async _initialize(
    cardFieldSelectors: CardFieldSelectors,
    language: string
  ): Promise<PaymentAvailability> {
    await this._teardownInstances();

    this._language = language;
    this._payPalLocale = payPalLocaleFor(language);

    const clientToken = await firstValueFrom(this.getClientToken());
    this._client = await client.create({ authorization: clientToken.token });

    this._dataCollector = await this._createDataCollector();
    this._hostedFields = await hostedFields.create({
      client: this._client,
      fields: this._buildHostedFields(cardFieldSelectors, language),
      styles: this._hostedFieldStyles(),
    });
    this._attachCardFieldListeners();

    const [paypalAvailable, venmoAvailable, applePayAvailable] = await Promise.all([
      this._initializePayPal(),
      this._initializeVenmo(clientToken.profileId),
      this._initializeApplePay(),
    ]);

    return {
      card: true,
      paypal: paypalAvailable,
      venmo: venmoAvailable,
      applePay: applePayAvailable,
    };
  }

  private async _createDataCollector(): Promise<DataCollector | null> {
    if (!this._client) {
      return null;
    }

    try {
      return await dataCollector.create({ client: this._client, paypal: true });
    } catch (error) {
      console.warn("Braintree device data collection is unavailable.", error);
      return null;
    }
  }

  private _buildHostedFields(
    selectors: CardFieldSelectors,
    language: string
  ): HostedFieldFieldOptions {
    const placeholders = hostedFieldPlaceholdersFor(language);
    const resolved = { ...DEFAULT_CARD_FIELD_SELECTORS, ...selectors };
    const fields: HostedFieldFieldOptions = {};

    Object.keys(resolved).forEach((key) => {
      const field = key as CardFieldValue;
      fields[field] = {
        container: resolved[field],
        placeholder: placeholders[field],
      };
    });

    return fields;
  }

  /**
   * Hosted Fields live in Braintree iframes, so CSS custom properties cannot
   * reach them. Resolve the brand tokens here and pass literal values across.
   */
  private _hostedFieldStyles(): Record<string, Record<string, string>> {
    const root = getComputedStyle(document.documentElement);
    const token = (name: string, fallback: string) =>
      root.getPropertyValue(name).trim() || fallback;

    return {
      input: {
        "font-size": token("--splash-input-font-size", "16px"),
        "font-family": token(
          "--cmnh-font-proxima-nova-font-family",
          "sans-serif"
        ),
        color: token("--cmnh-color-font-body", "#2f3336"),
      },
      ":focus": {
        color: token("--cmnh-color-dark", "#2f3336"),
      },
      ".invalid": {
        color: token("--cmnh-color-feedback-error", "#c8102e"),
      },
    };
  }

  private _attachCardFieldListeners(): void {
    this._hostedFields?.on("blur", (event) => {
      this._cardFieldHandler?.(event.emittedBy as CardFieldValue, {
        visited: true,
        ...this._fieldState(event),
      });
    });

    this._hostedFields?.on("validityChange", (event) => {
      this._cardFieldHandler?.(
        event.emittedBy as CardFieldValue,
        this._fieldState(event)
      );
    });
  }

  private _fieldState(event: HostedFieldsEvent): CardFieldState {
    const field = event.fields[event.emittedBy];
    return { empty: field.isEmpty, valid: field.isValid };
  }

  private async _initializePayPal(): Promise<boolean> {
    if (!this._client) {
      return false;
    }

    try {
      this._payPalCheckout = await paypalCheckout.create({
        client: this._client,
      });
      await this._payPalCheckout.loadPayPalSDK(this._payPalSdkOptions());
      return true;
    } catch (error) {
      console.warn("PayPal is unavailable.", error);
      this._payPalCheckout = null;
      return false;
    }
  }

  private _payPalSdkOptions(): PayPalCheckoutLoadPayPalSDKOptions {
    const options: PayPalCheckoutLoadPayPalSDKOptions = {
      vault: true,
      locale: this._payPalLocale,
    };
    const nonce = this._contentSecurityPolicyNonce();
    if (nonce) {
      options.dataAttributes = { "csp-nonce": nonce };
    }
    return options;
  }

  private _contentSecurityPolicyNonce(): string | null {
    const element = document.querySelector("[nonce]");
    if (!element) {
      return null;
    }

    const withNonce = element as HTMLElement & { nonce?: string };
    return withNonce.nonce || element.getAttribute("nonce");
  }

  private async _initializeVenmo(profileId?: string): Promise<boolean> {
    if (!this._client) {
      return false;
    }

    try {
      const options: VenmoCreateOptions = {
        client: this._client,
        requireManualReturn: false,
        // The return coordinator preserves the shareable URL and active checkout.
        ignoreHistoryChanges: true,
        allowDesktopWebLogin: true,
        // Checkout state is in memory, so app switching must return to this tab.
        allowNewBrowserTab: false,
        allowNonDefaultBrowsers: false,
        allowWebviews: false,
        paymentMethodUsage: "multi_use",
        profileId,
      };
      this._venmo = await venmo.create(options);

      if (!this._venmo.isBrowserSupported()) {
        await this._venmo.teardown();
        this._venmo = null;
        return false;
      }

      if (this._venmo.hasTokenizationResult()) {
        await this.authorizeVenmo();
      }

      return true;
    } catch (error) {
      console.warn("Venmo is unavailable.", error);
      this._venmo = null;
      return false;
    }
  }

  private async _initializeApplePay(): Promise<boolean> {
    const session = this._applePaySessionCtor();
    if (
      !session ||
      !session.supportsVersion(ApplePayVersion) ||
      !session.canMakePayments()
    ) {
      return false;
    }

    if (!this._client) {
      return false;
    }

    try {
      this._applePay = await applePay.create({ client: this._client });
      return true;
    } catch (error) {
      console.warn("Apple Pay is unavailable.", error);
      this._applePay = null;
      return false;
    }
  }

  private _clearApplePayAuthorization(): void {
    this._payloads[DirectPaymentMethod.ApplePay] = undefined;
    this._applePayAuthorizedAmount = null;
  }

  private _closePayPalButtons(buttons: PayPalButtonsHandle): void {
    try {
      buttons.close();
    } catch {
      // Buttons may already be detached or never attached.
    }
  }

  private async _teardownInstances(): Promise<void> {
    this._venmoReturn.clear();
    this._payloads = {};
    this._applePayAuthorizedAmount = null;

    if (this._applePaySession) {
      try {
        this._applePaySession.abort();
      } catch {
        // The sheet may already be closed.
      }
      this._applePaySession = null;
    }

    if (this._payPalButtons) {
      this._closePayPalButtons(this._payPalButtons);
      this._payPalButtons = null;
    }

    await Promise.all([
      this._safeTeardown(this._hostedFields),
      this._safeTeardown(this._payPalCheckout),
      this._safeTeardown(this._venmo),
      this._safeTeardown(this._applePay),
      this._safeTeardown(this._dataCollector),
    ]);

    this._hostedFields = null;
    this._payPalCheckout = null;
    this._venmo = null;
    this._applePay = null;
    this._dataCollector = null;
    this._client = null;
  }

  /** Shuts down a Braintree SDK object if it is present and has teardown(). */
  private async _safeTeardown(instance: unknown): Promise<void> {
    if (!this._hasTeardown(instance)) {
      return;
    }

    try {
      await instance.teardown();
    } catch (error) {
      console.warn("Could not tear down a Braintree instance.", error);
    }
  }

  private _hasTeardown(
    instance: unknown
  ): instance is { teardown: () => unknown } {
    return (
      instance !== null &&
      typeof instance === "object" &&
      "teardown" in instance &&
      typeof instance.teardown === "function"
    );
  }

  private _paypalSdkFromWindow(): PayPalSdk | undefined {
    return (window as unknown as { paypal?: PayPalSdk }).paypal;
  }

  private _applePaySessionCtor(): typeof ApplePaySession | undefined {
    return (window as unknown as { ApplePaySession?: typeof ApplePaySession })
      .ApplePaySession;
  }
}
